import { datadogRum } from '@datadog/browser-rum';
import type { DatadogConfig } from '@ringba/config';
import { createBeforeSend } from './beforeSend';
import { logger, markRumActive } from './state';
import type { InitRumOptions } from './types';

/**
 * Datadog RUM — client observability (errors, performance, sessions).
 * No-ops without `config.clientToken`/`config.applicationId`.
 */
export function initRum(config: DatadogConfig | null | undefined, options: InitRumOptions): void {
  if (!config?.clientToken || !config.applicationId) {
    logger.log('No Datadog config — skipping RUM init.');
    return;
  }

  try {
    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate ?? 0,
      trackFeatureFlagsForEvents: config.trackFeatureFlagsForEvents,
      trackUserInteractions: true,
      // Under `mask`, a click is named only by a `data-testid` on its element or an ancestor; every other name is a placeholder, so no rendered text leaves the browser.
      defaultPrivacyLevel: 'mask',
      actionNameAttribute: 'data-testid',
      beforeSend: createBeforeSend(config, options),
    });
    markRumActive();
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (navigation) datadogRum.setViewContextProperty('navigationType', navigation.type);
    logger.log('RUM initialized', {
      service: config.service,
      site: config.site,
      env: config.env,
      sample: config.sessionSampleRate ?? 0,
      suppressErrors: config.suppressErrors,
    });
  } catch (err) {
    logger.warn('RUM init failed — continuing without client observability.', err);
  }
}
